'use client';

import type { ArchitectureState } from '@/lib/architecture-state';

interface BudgetMeterProps {
  budget: ArchitectureState['budget'];
}

function fmtCount(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(2)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(0)}K`;
  return String(n);
}

function zoneClass(pct: number): { bar: string; text: string; label?: string } {
  if (pct >= 0.95) return { bar: 'bg-red-400 shadow-[0_0_8px_rgba(248,113,113,0.6)]', text: 'text-red-300', label: 'P2 auto-skip active' };
  if (pct >= 0.80) return { bar: 'bg-amber-300', text: 'text-amber-300' };
  return { bar: 'bg-emerald-400', text: 'text-emerald-300' };
}

interface RowProps {
  label: string;
  used: number;
  cap: number;
  pct: number;
}

function BudgetRow({ label, used, cap, pct }: RowProps): React.JSX.Element {
  const zone = zoneClass(pct);
  const widthPct = Math.min(100, pct * 100);

  return (
    <div className="flex items-center gap-2">
      <div className="w-12 text-[9px] uppercase tracking-wider text-white/60">{label}</div>
      <div className="flex-1 h-1.5 rounded-full bg-zinc-800 overflow-hidden">
        <div
          className={`h-full ${zone.bar} transition-[width] duration-500 ease-out`}
          style={{ width: `${widthPct.toFixed(1)}%` }}
        />
      </div>
      <div className="w-24 text-right text-[9px] tabular-nums text-white/70">
        {fmtCount(used)}/{fmtCount(cap)}
      </div>
      <div className={`w-10 text-right text-[10px] tabular-nums font-semibold ${zone.text}`}>
        {(pct * 100).toFixed(0)}%
      </div>
    </div>
  );
}

export function BudgetMeter({ budget }: BudgetMeterProps): React.JSX.Element {
  return (
    <div className="rounded border border-zinc-800 bg-zinc-900/80 p-2">
      <div className="text-[9px] uppercase tracking-wider text-white/40 mb-1.5">Daily budget</div>
      <div className="space-y-1.5">
        <BudgetRow label="SAPI" used={budget.sapi.used} cap={budget.sapi.cap} pct={budget.sapi.pct} />
        <BudgetRow label="FMP" used={budget.fmp.used} cap={budget.fmp.cap} pct={budget.fmp.pct} />
      </div>
    </div>
  );
}
